import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

type UpdateOrgSlugRpcClient = {
  rpc(
    fn: 'update_org_slug',
    args: { _org_id: string; _new_slug: string },
  ): Promise<{ data: { slug: string } | null; error: Error | null }>;
};

/** Public tenant domain. Website address = `<slug>.{TENANT_DOMAIN}`. */
export const TENANT_DOMAIN = 'thepolishedversion.com';

/** Owners may change their website address (slug) at most once per this window. */
export const SLUG_CHANGE_COOLDOWN_DAYS = 30;
const COOLDOWN_MS = SLUG_CHANGE_COOLDOWN_DAYS * 24 * 60 * 60 * 1000;

/** Subdomains that can never be used as a tenant slug. Mirrors the DB + OrgContext. */
export const RESERVED_SLUGS = new Set([
  'www', 'app', 'api', 'admin', 'mail', 'm', 'demo', 'auth', 'signup',
]);

/**
 * Deterministic slugify — kept in sync with `slugify` in src/pages/Signup.tsx
 * and `public.slugify_text` in the org-slug-cooldown migration.
 */
export const slugify = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 40);

export const buildWebsiteUrl = (slug: string): string => `https://${slug}.${TENANT_DOMAIN}`;

export interface OrgSlugInfo {
  slug: string;
  slugLastChangedAt: string | null;
  /** True while the 30-day cooldown is still active. */
  locked: boolean;
  /** ISO timestamp the slug may next be changed, or null if changeable now. */
  nextChangeAllowedAt: string | null;
}

/**
 * Reads the active org's slug and cooldown state so the admin Company Info page
 * can show the website address and gate edits behind the 30-day window.
 */
export function useOrgSlugInfo(orgId: string | null | undefined) {
  return useQuery({
    queryKey: ['org-slug-info', orgId],
    enabled: Boolean(orgId),
    queryFn: async (): Promise<OrgSlugInfo | null> => {
      const { data, error } = await supabase
        .from('organizations')
        .select('slug, slug_last_changed_at')
        .eq('id', orgId!)
        .maybeSingle();

      if (error) throw error;
      if (!data) return null;

      const lastChanged = data.slug_last_changed_at;
      const nextAllowedMs = lastChanged ? new Date(lastChanged).getTime() + COOLDOWN_MS : 0;
      const locked = nextAllowedMs > Date.now();

      return {
        slug: data.slug,
        slugLastChangedAt: lastChanged,
        locked,
        nextChangeAllowedAt: locked ? new Date(nextAllowedMs).toISOString() : null,
      };
    },
  });
}

export interface UpdateOrgSlugInput {
  orgId: string;
  slug: string;
}

/**
 * Changes the tenant website address via the SECURITY DEFINER
 * `update_org_slug` RPC, which enforces ownership, uniqueness, reserved names,
 * and the 30-day cooldown server-side. The RPC raises a descriptive error that
 * the caller surfaces to the owner.
 */
export function useUpdateOrgSlug() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ orgId, slug }: UpdateOrgSlugInput) => {
      // RPC is not in the generated types until the migration is applied.
      const { data, error } = await (supabase as unknown as UpdateOrgSlugRpcClient).rpc('update_org_slug', {
        _org_id: orgId,
        _new_slug: slug,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (_result, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: ['org-slug-info', orgId] });
      queryClient.invalidateQueries({ queryKey: ['org-profile'] });
    },
  });
}
